import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { getHttpErrorToastMessage } from '../utils/http-error.util';
import { UtilityService } from './utility.service';

/** The `<p-toast>` in AppComponent that shows error toasts when standalone. */
export const ERROR_TOAST_KEY = 'error';
export const ERROR_TOAST_LIFE = 8000;
/** The same message again within this window is dropped. */
export const ERROR_TOAST_DEDUPE_MS = 8000;

/**
 * One calm toast per failure. Embedded in HCP, the toast goes to HCP: the
 * iframe is sized to its content, so a fixed toast inside it can sit off
 * screen.
 */
@Injectable({
  providedIn: 'root'
})
export class ErrorToastService {
  private readonly messageService = inject(MessageService);
  private readonly utilityService = inject(UtilityService);
  private readonly translocoService = inject(TranslocoService);

  private readonly shownAt = new Map<string, number>();

  showHttpError(error: HttpErrorResponse, method: string) {
    const message = getHttpErrorToastMessage(error, method);
    this.show('key' in message ? this.translate(message) : message.text);
  }

  private translate({ key, what }: { key: string; what?: string }): string {
    const name = what ? this.translocoService.translate(what) : undefined;
    return this.translocoService.translate(key, { what: name });
  }

  show(message: string) {
    const now = Date.now();
    for (const [shown, at] of this.shownAt) {
      if (now - at >= ERROR_TOAST_DEDUPE_MS) this.shownAt.delete(shown);
    }
    if (this.shownAt.has(message)) return;
    this.shownAt.set(message, now);

    if (this.isEmbedded()) {
      this.utilityService.emitToast({ message, isSuccess: false });
    } else {
      this.messageService.add({
        key: ERROR_TOAST_KEY,
        severity: 'error',
        detail: message,
        life: ERROR_TOAST_LIFE,
        closable: true
      });
    }
  }

  isEmbedded() {
    return window.parent !== window;
  }
}
